import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Monitor, Smartphone, Upload } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { Spinner } from "../components/kit";

export const VAR_RX = /\{\{\s*([a-zA-Z_][a-zA-Z0-9_]{0,40})\s*\}\}/g;
export const findVars = (s) => [...new Set([...(s || "").matchAll(VAR_RX)].map((m) => m[1]))];

export function ColorField({ label, value, onChange, testid }) {
  return (
    <label className="flex items-center justify-between gap-3 py-1.5">
      <span className="text-[13px] text-[#334155]">{label}</span>
      <span className="flex items-center gap-1.5">
        <input type="color" value={/^#[0-9a-f]{6}$/i.test(value) ? value : "#000000"} onChange={(e) => onChange(e.target.value.toUpperCase())} className="h-8 w-8 rounded border border-[#E2E8F0] cursor-pointer bg-white p-0.5" data-testid={`${testid}-picker`} />
        <input className="input mono h-8 w-[92px] text-xs" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
      </span>
    </label>
  );
}

export function UploadButton({ kind, onUploaded, label = "Upload", testid }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const pick = async (file) => {
    if (!file) return;
    setBusy(true);
    try { const fd = new FormData(); fd.append("file", file); fd.append("kind", kind); const { data } = await api.post("/email/assets", fd); onUploaded(data.url); toast.success("Image uploaded"); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); ref.current.value = ""; }
  };
  return (
    <>
      <input ref={ref} type="file" accept=".png,.jpg,.jpeg,.webp,.gif" className="hidden" onChange={(e) => pick(e.target.files?.[0])} data-testid={`${testid}-input`} />
      <button type="button" className="btn-secondary btn-sm" onClick={() => ref.current.click()} disabled={busy} data-testid={testid}>{busy ? <Spinner size={12} /> : <Upload size={12} />}{label}</button>
    </>
  );
}

export function EmailPreview({ payload, testid = "email-preview" }) {
  const [device, setDevice] = useState("desktop");
  const [res, setRes] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const key = JSON.stringify(payload);
  useEffect(() => {
    let live = true;
    setBusy(true);
    const t = setTimeout(async () => {
      try { const { data } = await api.post("/email/render", payload); if (live) { setRes(data); setErr(null); } }
      catch (e) { if (live) setErr(errMsg(e)); } finally { if (live) setBusy(false); }
    }, 350);
    return () => { live = false; clearTimeout(t); };
  }, [key]); // eslint-disable-line
  return (
    <div className="flex flex-col h-full" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <div className="inline-flex rounded-md border border-[#E2E8F0] bg-white p-0.5">
          {[["desktop", Monitor], ["mobile", Smartphone]].map(([d, I]) => <button key={d} onClick={() => setDevice(d)} className={`h-7 px-2.5 rounded text-xs inline-flex items-center gap-1.5 transition-colors ${device === d ? "bg-[#0A0A0A] text-white" : "text-[#475569] hover:bg-[#F1F4FA]"}`} data-testid={`preview-device-${d}`}><I size={12} />{d}</button>)}
        </div>
        {busy && <span className="text-[11px] muted inline-flex items-center gap-1"><Spinner size={11} />rendering</span>}
        {res?.missing?.length > 0 && <span className="text-[11px] text-amber-700 ml-auto" data-testid="preview-missing">Missing: {res.missing.join(", ")}</span>}
      </div>
      {res?.subject && <div className="rounded-t-lg border border-b-0 border-[#E2E8F0] bg-white px-4 py-2.5 text-[13px]" data-testid="preview-subject"><span className="muted">Subject: </span><span className="font-medium">{res.subject}</span></div>}
      {err && <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2 mb-2" data-testid="preview-error">{err}</div>}
      <div className={`flex-1 border border-[#E2E8F0] bg-[#EEF1F6] ${res?.subject ? "rounded-b-lg" : "rounded-lg"} p-4 overflow-auto`}>
        <iframe title="Email preview" sandbox="" srcDoc={res?.html || ""} className="mx-auto block bg-white rounded shadow-sm transition-[width] duration-300 min-h-[560px] h-full" style={{ width: device === "mobile" ? 375 : "100%", maxWidth: 680 }} data-testid="preview-iframe" />
      </div>
    </div>
  );
}
